/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { invoke, construct } from "./invoke.js";
import { createOnAndOffNamespaceListeners, } from "./utils.js";
/**
 * Methods for interacting with ToDesktop notifications
 *
 * @remarks
 * This package exposes a number of methods that allow you to create and manipulate OS-level notifications.
 *
 * @experimental
 * @public
 * @packageDocumentation
 */
/**
 * Creates a new Notification.
 *
 * @param options - Notification constructor options.
 * @returns Identifier for the newly created Notification.
 * @public
 */
export async function create(options) {
    return construct({
        namespace: "Notification",
        args: [options],
    });
}
/**
 * Shows a notification to the user
 *
 * @param options - ref: Reference for the Notification.
 * @public
 */
export async function show({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        method: "show",
        args: [],
    });
}
/**
 * Dismisses a notification that was shown to the user
 *
 * @param options - ref: Reference for the Notification.
 * @public
 */
export async function close({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        method: "close",
        args: [],
    });
}
/**
 * Retrieves the title of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getTitle({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "title",
    });
}
/**
 * Retrieves the subtitle of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getSubtitle({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "subtitle",
    });
}
/**
 * Retrieves the body of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getBody({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "body",
    });
}
/**
 * Retrieves the reply placeholder of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getReplyPlaceholder({ ref, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "replyPlaceholder",
    });
}
/**
 * Retrieves the sound of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getSound({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "sound",
    });
}
/**
 * Retrieves the close button text of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getCloseButtonText({ ref, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "closeButtonText",
    });
}
/**
 * Retrieves whether the notification is silent.
 *
 * @param options - ref: Reference for the Notification.
 * @returns boolean.
 * @public
 */
export async function getSilent({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "silent",
    });
}
/**
 * Retrieves whether the notification has a reply action.
 *
 * @param options - ref: Reference for the Notification.
 * @returns boolean.
 * @public
 */
export async function getHasReply({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "hasReply",
    });
}
/**
 * Retrieves the urgency of the notification. Can be "normal", "critical", or "low".
 * Linux only.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getUrgency({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "urgency",
    });
}
/**
 * Retrieves the type of timeout duration for the notification. Can be "default" or "never".
 * Linux and Windows only.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getTimeoutType({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "timeoutType",
    });
}
/**
 * Retrieves the actions of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getActions({ ref, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "actions",
    });
}
/**
 * Retrieves the Toast XML of the notification.
 *
 * @param options - ref: Reference for the Notification.
 * @returns string.
 * @public
 */
export async function getToastXml({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "Notification",
        property: "toastXml",
    });
}
const listeners = createOnAndOffNamespaceListeners("Notification");
/**
 * Subcribes to an event on a notification object.
 *
 * @param eventName - The event name to subscribe to.
 * @param callback - The callback function to execute when the event occurs.
 * @param options - ref: Reference for the Notification.
 * @returns unsubscribe callback.
 * @public
 */
export const on = listeners.on;
/**
 * Unsubscribes all notification objects from the event name.
 *
 * @param options - ref: Reference for the Notification; eventName: The event name to unsubscribe from;
 * @returns Reference for the notification.
 * @public
 */
export const removeAllListeners = listeners.removeAllListeners;
